import i18n from 'modules/core/lib/i18n'

import { type StatusColor } from './statusColor'

// Simplified type matching what's actually fetched in the GraphQL query
type SpendingAlert = {
  id: number
  status: string
  type: string
  reason?: string | null
}

type AlertDisplay = {
  color: StatusColor
  label: string
} | null

export function getSpendingAlertsDisplay(
  spendingAlertsTarget: SpendingAlert[] | null | undefined = [],
  spendingAlertsSource: SpendingAlert[] | null | undefined = []
): AlertDisplay {
  const allAlerts = [...(spendingAlertsTarget || []), ...(spendingAlertsSource || [])]
  const failingAlerts = allAlerts.filter(
    (alert) => alert?.status === 'failed' && alert?.type !== 'in_policy'
  )
  const inPolicyAlert = allAlerts.find((alert) => alert?.type === 'in_policy')

  if (failingAlerts.length === 0) {
    if (!inPolicyAlert) {
      return null
    }
    return {
      color: 'positive',
      label: i18n.t('expenses_v2.expense.spending_alert.label.in_policy'),
    }
  }

  // Get unique alerts by type
  const uniqueAlertsByType = failingAlerts.reduce<SpendingAlert[]>((acc, alert) => {
    //we want to show all the alerts from the recommendation
    if (alert && !acc.some((a) => a.type === alert.type && a.type !== 'policy_compliance')) {
      acc.push(alert)
    }
    return acc
  }, [])

  // we always use warning color, out of policy alert is not blocking
  const color = 'warning'

  // Generate label
  let label: string

  if (uniqueAlertsByType.length === 1 && uniqueAlertsByType[0]) {
    const alertType = uniqueAlertsByType[0].type
    switch (alertType) {
      case 'policy_compliance': {
        const reason = uniqueAlertsByType[0].reason
        label =
          reason?.split(':')?.[0]?.trim() ??
          i18n.t('expenses_v2.expense.spending_alert.label.policy_compliance')
        break
      }
      case 'spending_policy':
        label = i18n.t('expenses_v2.expense.spending_alert.label.spending_policy')
        break
      case 'mileage_rate_changed':
        label = i18n.t('expenses_v2.expense.spending_alert.label.mileage_rate_changed')
        break
      case 'duplicate_expense':
        label = i18n.t('expenses_v2.expense.spending_alert.label.duplicate_expense')
        break
      case 'category_conflict':
        label = i18n.t('expenses_v2.expense.spending_alert.label.category_conflict')
        break
      default:
        label = alertType
    }
  } else {
    // Multiple alerts
    label = `${uniqueAlertsByType.length} ${i18n.t(
      'expenses_v2.expense.spending_alert.label.multiple'
    )}`
  }

  return { color, label }
}
